import { downloadFile, requestJson, requestMultipart } from "@/lib/api";

const BASE = "api/v1/admin/establishments";

/** Estabelecimento próprio da Naturexpress (destinatário das NFs de entrada / dono do certificado A1). */
export interface Establishment {
  id: string;
  cnpj: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function listEstablishments(): Promise<Establishment[]> {
  return requestJson<Establishment[]>(BASE);
}

/** Cadastra um estabelecimento (CNPJ + nome). O backend valida unicidade do CNPJ ativo. */
export async function createEstablishment(cnpj: string, name: string): Promise<Establishment> {
  return requestJson<Establishment>(BASE, { method: "POST", body: { cnpj, name } });
}

export async function updateEstablishment(id: string, cnpj: string, name: string): Promise<Establishment> {
  return requestJson<Establishment>(`${BASE}/${encodeURIComponent(id)}`, { method: "PUT", body: { cnpj, name } });
}

export async function deactivateEstablishment(id: string): Promise<void> {
  await requestJson<void>(`${BASE}/${encodeURIComponent(id)}/deactivate`, { method: "POST" });
}

// ---------------------------------------------------------------- Detalhe do estabelecimento

/** CNAE (atividade econômica) declarado — o principal e os secundários. */
export interface EstablishmentCnae {
  code: string;
  description: string;
  primary: boolean;
}

/** Regime tributário vigente/histórico (uma faixa de vigência por versão). */
export interface TaxRegimeResponse {
  id: string;
  regime: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Perfil cadastral completo do estabelecimento (carregado sob demanda no detalhe). */
export interface EstablishmentDetailResponse {
  id: string;
  cnpj: string;
  name: string;
  tradeName: string | null;
  active: boolean;
  predecessorId: string | null;
  stateRegistration: string | null;
  municipalRegistration: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  website: string | null;
  addressZipCode: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressCountry: string | null;
  cnaes: EstablishmentCnae[];
  currentTaxRegime: TaxRegimeResponse | null;
  createdAt: string;
  updatedAt: string;
}

/** Corpo do PUT /profile — identidade/endereço/contatos + CNAEs (substitui a lista). NÃO inclui o CNPJ. */
export interface EstablishmentProfileRequest {
  name?: string | null;
  tradeName?: string | null;
  stateRegistration?: string | null;
  municipalRegistration?: string | null;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  website?: string | null;
  addressZipCode?: string | null;
  addressStreet?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  addressDistrict?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressCountry?: string | null;
  cnaes?: EstablishmentCnae[];
}

export async function getEstablishment(id: string): Promise<EstablishmentDetailResponse> {
  return requestJson<EstablishmentDetailResponse>(`${BASE}/${encodeURIComponent(id)}`);
}

/** Atualiza identidade/endereço/contatos + CNAEs. Nunca troca o CNPJ (use succeedCnpj). */
export async function updateEstablishmentProfile(
  id: string,
  body: EstablishmentProfileRequest,
): Promise<EstablishmentDetailResponse> {
  return requestJson<EstablishmentDetailResponse>(`${BASE}/${encodeURIComponent(id)}/profile`, {
    method: "PUT",
    body,
  });
}

/** Cria um estabelecimento SUCESSOR com um novo CNPJ (a troca de CNPJ não é edição, é sucessão). */
export async function succeedCnpj(id: string, newCnpj: string, name?: string): Promise<Establishment> {
  return requestJson<Establishment>(`${BASE}/${encodeURIComponent(id)}/succeed-cnpj`, {
    method: "POST",
    body: name ? { newCnpj, name } : { newCnpj },
  });
}

// ---------------------------------------------------------------- Regime tributário

export async function listTaxRegimes(id: string): Promise<TaxRegimeResponse[]> {
  return requestJson<TaxRegimeResponse[]>(`${BASE}/${encodeURIComponent(id)}/tax-regimes`);
}

export async function addTaxRegime(
  id: string,
  body: { regime: string; effectiveFrom: string; note?: string | null },
): Promise<TaxRegimeResponse> {
  return requestJson<TaxRegimeResponse>(`${BASE}/${encodeURIComponent(id)}/tax-regimes`, {
    method: "POST",
    body,
  });
}

// ---------------------------------------------------------------- Dados bancários (registro puro, sem cobrança)

export interface BankAccountResponse {
  id: string;
  bankCode: string | null;
  bankName: string | null;
  agency: string | null;
  accountNumber: string | null;
  accountType: string | null;
  holderName: string | null;
  holderDocument: string | null;
  note: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccountRequest {
  bankCode?: string | null;
  bankName?: string | null;
  agency?: string | null;
  accountNumber?: string | null;
  accountType?: string | null;
  holderName?: string | null;
  holderDocument?: string | null;
  note?: string | null;
}

export async function listBankAccounts(id: string): Promise<BankAccountResponse[]> {
  return requestJson<BankAccountResponse[]>(`${BASE}/${encodeURIComponent(id)}/bank-accounts`);
}

export async function createBankAccount(id: string, body: BankAccountRequest): Promise<BankAccountResponse> {
  return requestJson<BankAccountResponse>(`${BASE}/${encodeURIComponent(id)}/bank-accounts`, {
    method: "POST",
    body,
  });
}

export async function updateBankAccount(
  id: string,
  accId: string,
  body: BankAccountRequest,
): Promise<BankAccountResponse> {
  return requestJson<BankAccountResponse>(
    `${BASE}/${encodeURIComponent(id)}/bank-accounts/${encodeURIComponent(accId)}`,
    { method: "PUT", body },
  );
}

export async function deactivateBankAccount(id: string, accId: string): Promise<void> {
  await requestJson<void>(
    `${BASE}/${encodeURIComponent(id)}/bank-accounts/${encodeURIComponent(accId)}/deactivate`,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------- Chaves Pix (registro puro, sem cobrança)

export type PixKeyType = "CPF" | "CNPJ" | "EMAIL" | "TELEFONE" | "ALEATORIA";

export interface PixKeyResponse {
  id: string;
  keyType: PixKeyType;
  keyValue: string;
  description: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PixKeyRequest {
  keyType: PixKeyType;
  keyValue: string;
  description?: string | null;
}

export async function listPixKeys(id: string): Promise<PixKeyResponse[]> {
  return requestJson<PixKeyResponse[]>(`${BASE}/${encodeURIComponent(id)}/pix-keys`);
}

export async function createPixKey(id: string, body: PixKeyRequest): Promise<PixKeyResponse> {
  return requestJson<PixKeyResponse>(`${BASE}/${encodeURIComponent(id)}/pix-keys`, { method: "POST", body });
}

export async function updatePixKey(id: string, pixId: string, body: PixKeyRequest): Promise<PixKeyResponse> {
  return requestJson<PixKeyResponse>(
    `${BASE}/${encodeURIComponent(id)}/pix-keys/${encodeURIComponent(pixId)}`,
    { method: "PUT", body },
  );
}

export async function deactivatePixKey(id: string, pixId: string): Promise<void> {
  await requestJson<void>(
    `${BASE}/${encodeURIComponent(id)}/pix-keys/${encodeURIComponent(pixId)}/deactivate`,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------- Documentos (versionados)

export type EstablishmentDocType = "CARTAO_CNPJ" | "CONTRATO_SOCIAL" | "ALTERACAO_CONTRATUAL";

export interface DocumentResponse {
  id: string;
  docType: EstablishmentDocType;
  title: string | null;
  fileName: string | null;
  contentType: string | null;
  contentSha256: string;
  sizeBytes: number;
  version: number;
  active: boolean;
  note: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  supersedesId: string | null;
  supersededBy: string | null;
  supersededAt: string | null;
}

export async function listDocuments(id: string): Promise<DocumentResponse[]> {
  return requestJson<DocumentResponse[]>(`${BASE}/${encodeURIComponent(id)}/documents`);
}

/** Envia (ou substitui, via replaceDocumentId) um documento. Multipart: file + docType + title?/note?. */
export async function uploadDocument(
  id: string,
  args: { file: File; docType: EstablishmentDocType; title?: string; replaceDocumentId?: string; note?: string },
): Promise<DocumentResponse> {
  const form = new FormData();
  form.append("file", args.file);
  form.append("docType", args.docType);
  if (args.title) form.append("title", args.title);
  if (args.replaceDocumentId) form.append("replaceDocumentId", args.replaceDocumentId);
  if (args.note) form.append("note", args.note);
  return requestMultipart<DocumentResponse>(`${BASE}/${encodeURIComponent(id)}/documents`, form);
}

export async function downloadDocument(id: string, docId: string, fallbackName: string): Promise<void> {
  await downloadFile(
    `${BASE}/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/download`,
    fallbackName,
  );
}

// ---------------------------------------------------------------- Extração assistida (cartão CNPJ)

export interface CartaoCnpjCnae {
  code: string;
  description: string;
}

/** Prévia da extração do cartão CNPJ — NÃO persiste nada; a tela decide o que aplicar. */
export interface CartaoCnpjExtractionPreview {
  textExtractable: boolean;
  cnpj: string | null;
  name: string | null;
  tradeName: string | null;
  stateRegistration: string | null;
  addressZipCode: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressState: string | null;
  email: string | null;
  phone: string | null;
  cnaePrincipal: CartaoCnpjCnae | null;
  cnaesSecundarios: CartaoCnpjCnae[];
}

/** Extrai os campos de um cartão CNPJ em PDF para revisão (não grava nada). */
export async function extractCartaoCnpj(id: string, file: File): Promise<CartaoCnpjExtractionPreview> {
  const form = new FormData();
  form.append("file", file);
  return requestMultipart<CartaoCnpjExtractionPreview>(
    `${BASE}/${encodeURIComponent(id)}/documents/extract-cartao-cnpj`,
    form,
  );
}
